import { uuidv7 } from "uuidv7"

import type { SearchAutocompleteResponseDatagramBuf } from "~/classes/generated/response"
import type { SearchAutocompleteRequestDatagram } from "~/models/polyschema/SearchAutocompleteRequestDatagram"
import type { SearchAutocompleteResponseDatagram } from "~/models/polyschema/SearchAutocompleteResponseDatagram"

import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SearchAutocompleteRequestDatagramBuf } from "~/classes/generated/request"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { InterfaceUser } from "~/classes/users/InterfaceUser"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { parseSearchId } from "~/utils/flatbuffers/id"
import { addSearchToBuf, parseSearch } from "~/utils/flatbuffers/search"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/find", theme.colors.teal.hex)

export function sendSearchAutocompleteRequest(request: SearchAutocompleteRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const search = addSearchToBuf(builder, request.search)
  const requestId = addRequestIdToBuf(builder, request.requestId)
  const fileSelection = addEntityPropertySelectionToBuf(builder, request.fileSelection)
  const pathSelection = addEntityPropertySelectionToBuf(builder, request.pathSelection)
  SearchAutocompleteRequestDatagramBuf.startSearchAutocompleteRequestDatagramBuf(builder)
  SearchAutocompleteRequestDatagramBuf.addRequestId(builder, requestId)
  SearchAutocompleteRequestDatagramBuf.addOffline(builder, request.offline)
  SearchAutocompleteRequestDatagramBuf.addSearch(builder, search)
  SearchAutocompleteRequestDatagramBuf.addOffset(builder, request.offset)
  SearchAutocompleteRequestDatagramBuf.addLimit(builder, request.limit)
  SearchAutocompleteRequestDatagramBuf.addFileSelection(builder, fileSelection)
  SearchAutocompleteRequestDatagramBuf.addPathSelection(builder, pathSelection)

  const buf = SearchAutocompleteRequestDatagramBuf.endSearchAutocompleteRequestDatagramBuf(builder)
  RequestDatagramBuf.startRequestDatagramBuf(builder)
  RequestDatagramBuf.addSearchAutocompleteRequest(builder, buf)

  const requests = DatagramBuf.createRequestVector(builder, [RequestDatagramBuf.endRequestDatagramBuf(builder)])
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requests)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function handleSearchAutocompleteResponseDatagramBuf(datagram: SearchAutocompleteResponseDatagramBuf) {
  onSearchAutocompleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    searchId: parseSearchId(datagram.searchId()!),
    search: maybe(parseSearch)(datagram.search()),
    files: isDefined(datagram.error()) ? undefined : parseVector(n => parseFile(datagram.files(n)!), datagram.filesLength()),
    offline: datagram.offline(),
  })
}

export function onSearchAutocompleteResponse(response: SearchAutocompleteResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  // TODO: We return the searchAutocompleteResponse with the file virtual properties,
  // in contrast to the search list response. We should figure out a more elegant way to
  // distribute the information.

  if (response.error || !response.files || !response.search) {
    emitInterfaceEvent("searchAutocompleteResponse", response, response.requestId)
    return onSearchAutocompleteErrored(response.error!)
  }

  emitEntityEvent("searchProperties", response.searchId, response.search, response.offline)

  // we need to add the file virtual search relevance property here for the UI to be able to
  // sort and whatnot for files. We only want to add the property to the files returned to
  // the 'search result' entity event, however.
  const iSearch = new StaticSearch(response.search)
  const results = iSearch.searchResults()
  if (len(results) !== len(response.files))
    console.error("Search results is malformed", response, results)

  // This gives us an array of search results per file in case there are multiple
  const resultsByFile = groupedDict(Object.values(results), "fileId")

  // send all of our file events based on the contents of this page
  const searchId = response.searchId
  for (const file of response.files) {
    emitEntityEvent("fileProperties", file.id, file, response.offline)

    const fileResults = resultsByFile[file.id]
    if (!isDefined(fileResults) || !fileResults.length) {
      console.error("No search results for file", file, results)
      continue
    }

    // get the top score to place in search result property. This isn't
    // strictly necessary since opensearch doesn't duplicate objects in
    // its search results.
    const result = maxElement(fileResults, r => r.score)!

    file.properties[PropertyId.FILE_VIRTUAL_SEARCH_RESULT] = {
      id: file.id,
      propertyId: PropertyId.FILE_VIRTUAL_SEARCH_RESULT,
      value: { type: PropertySchema.SearchResultValue, result },
      timestamp: uuidv7(),
      status: PropertyStatus.StatusCurrent,
      schema: PropertySchema.SearchResultValue,
      version: 0,
      author: InterfaceUser.generateClientOnlyUserId(),
    }

    // aggregate all matched chunks for this file in the search results
    const matchedChunks = fileResults.flatMap(r => r.matchedChunks ?? [])
    file.properties[PropertyId.FILE_VIRTUAL_MATCHED_CHUNKS] = {
      id: file.id,
      propertyId: PropertyId.FILE_VIRTUAL_MATCHED_CHUNKS,
      value: { type: PropertySchema.FileMatchedChunksValue, matchedChunks },
      timestamp: uuidv7(),
      status: PropertyStatus.StatusCurrent,
      schema: PropertySchema.FileMatchedChunksValue,
      version: 0,
      author: InterfaceUser.generateClientOnlyUserId(),
    }

    emitEntityEvent("searchResult", searchId, file, response.offline)
  }

  emitInterfaceEvent("searchAutocompleteResponse", response, response.requestId)
}

export function onSearchAutocompleteErrored(error: DatagramError) {
  console.error("Search autocomplete errored", error)
}
